import { parseDate } from "@internationalized/date";
import { ExternalLink } from "lucide-react";
import { Group, I18nProvider } from "react-aria-components";
import { Controller, useFormContext } from "react-hook-form";

import {
  ComboBox,
  ComboBoxButton,
  ComboBoxGroup,
  ComboBoxInput,
  ComboBoxListBox,
  ComboBoxListItem,
  ComboBoxPopover,
} from "@/components/combobox";
import { DatePicker } from "@/components/datepicker";
import { Input, Label, TextField } from "@/components/field";

function formatReadableDate(isoDateStr: null | string | undefined): string {
  if (!isoDateStr) return "";
  const parts = isoDateStr.split("-").map(Number);
  const year = parts[0] ?? 0;
  const month = parts[1] ?? 1;
  const day = parts[2] ?? 1;

  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "long",
    timeZone: "UTC",
    year: "numeric",
  }).format(new Date(Date.UTC(year, month - 1, day)));
}
import { SelectListItemLabel } from "@/components/select";
import { env } from "@/config/env";
import { useGetProjectDetailsOptions } from "@/features/projects/project/details/hooks/useGetProjectDetailsOptions";

import { useProjectDetails } from "../useProjectDetails";

export function ProductSettingsSection() {
  const { control } = useFormContext();
  const { isReadOnly, projectId } = useProjectDetails();
  const { data: options } = useGetProjectDetailsOptions(projectId);
  const products = options?.products || [];

  return (
    <div className="bg-bg-primary px-6">
      <h3 className="text-lg text-text-primary mb-3">Product Settings</h3>

      <div className="grid grid-cols-2 gap-x-6 gap-y-5">
        <Controller
          control={control}
          name="productId"
          render={({ field }) => (
            <ComboBox
              className="w-full"
              isDisabled={isReadOnly}
              onChange={(key) => field.onChange(key ?? null)}
              value={field.value}
            >
              <Label>Product</Label>
              <ComboBoxGroup>
                <ComboBoxInput placeholder="Select product" />
                <ComboBoxButton />
              </ComboBoxGroup>
              <ComboBoxPopover>
                <ComboBoxListBox items={products}>
                  {(item) => (
                    <ComboBoxListItem id={item.id} textValue={item.name}>
                      <SelectListItemLabel>{item.name}</SelectListItemLabel>
                    </ComboBoxListItem>
                  )}
                </ComboBoxListBox>
              </ComboBoxPopover>
            </ComboBox>
          )}
        />
        <Controller
          control={control}
          name="subscriptionId"
          render={({ field, fieldState }) => (
            <TextField
              isDisabled={isReadOnly}
              isInvalid={!!fieldState.error}
              name={field.name}
              onBlur={field.onBlur}
              onChange={field.onChange}
              value={field.value}
            >
              <Label>Subscription ID (sub_code)</Label>
              <Group
                className="isolate grid grid-cols-[1fr_calc(theme(size.6)+16px)]"
                data-ui="control"
              >
                <Input className="col-span-full row-start-1 pr-10" />
                {field.value && env.VITE_STRIPE_ACCOUNT && (
                  <a
                    className="z-10 -col-end-1 row-start-1 place-self-center text-text-tertiary hover:text-text-primary transition-colors"
                    href={`https://dashboard.stripe.com/${env.VITE_STRIPE_ACCOUNT}/subscriptions/${field.value}`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    <ExternalLink size={16} />
                  </a>
                )}
              </Group>
            </TextField>
          )}
        />

        <Controller
          control={control}
          name="renewalDate"
          render={({ field, fieldState }) =>
            isReadOnly ? (
              <TextField
                isDisabled
                isReadOnly
                value={formatReadableDate(field.value)}
              >
                <Label>Renewal Date</Label>
                <Input />
              </TextField>
            ) : (
              <I18nProvider locale="en-US">
                <DatePicker
                  errorMessage={fieldState.error?.message}
                  label="Renewal Date"
                  onChange={(date) =>
                    field.onChange(date ? date.toString() : "")
                  }
                  value={field.value ? parseDate(field.value) : null}
                />
              </I18nProvider>
            )
          }
        />

        <Controller
          control={control}
          name="cancelledDate"
          render={({ field, fieldState }) =>
            isReadOnly ? (
              <TextField
                isDisabled
                isReadOnly
                value={formatReadableDate(field.value)}
              >
                <Label>Cancelled Date</Label>
                <Input />
              </TextField>
            ) : (
              <I18nProvider locale="en-US">
                <DatePicker
                  errorMessage={fieldState.error?.message}
                  label="Cancelled Date"
                  onChange={(date) =>
                    field.onChange(date ? date.toString() : "")
                  }
                  value={field.value ? parseDate(field.value) : null}
                />
              </I18nProvider>
            )
          }
        />

        <Controller
          control={control}
          name="allowedCoupons"
          render={({ field, fieldState }) => (
            <TextField
              isDisabled={isReadOnly}
              isInvalid={!!fieldState.error}
              name={field.name}
              onBlur={field.onBlur}
              onChange={field.onChange}
              value={field.value}
            >
              <Label>Allowed Coupons</Label>
              <Input type="number" />
            </TextField>
          )}
        />

        <Controller
          control={control}
          name="closeInactiveRequestsAfterDays"
          render={({ field, fieldState }) => (
            <TextField
              isDisabled={isReadOnly}
              isInvalid={!!fieldState.error}
              name={field.name}
              onBlur={field.onBlur}
              onChange={field.onChange}
              value={field.value}
            >
              <Label>Close inactive requests after (days)</Label>
              <Input type="number" />
            </TextField>
          )}
        />
      </div>
    </div>
  );
}
